import { Component, Renderer2, effect, inject, OnInit, Signal, AfterViewInit, OnDestroy } from "@angular/core";
import { ActivatedRoute, NavigationEnd, Route, Router, RouterOutlet } from "@angular/router";
import { DOCUMENT } from "@angular/common";
import { ThemeService } from "../app/other/theme/theme.service";
import { BreakpointObserver, Breakpoints, BreakpointState } from '@angular/cdk/layout';
import { UserinfoFaqComponent } from "./admin/components/userinfo-faq/userinfo-faq.component";
import { WebsiteStateService } from "./state-management/services/website-state.service";
import { PageComponent } from "./pages/page/page.component";
import { DragDropModule } from "@angular/cdk/drag-drop";
import { ToggleEditModeComponent } from "./components/meta/toggle-edit-mode/toggle-edit-mode.component";
import { TestingComponent } from "./testing/testing/testing.component";
import { WebsiteConfigComponent } from "./components/meta/website-config/website-config.component";
import { Page, Theming } from "./state-management/model/interfaces/state.model";

import { isPlatformBrowser } from '@angular/common';
import { Inject, PLATFORM_ID } from '@angular/core';
import { Auth, onAuthStateChanged, User } from "@angular/fire/auth";
import { UserStateService } from "./state-management/services/user-state.service";
import { LogoutComponent } from "./admin/components/logout/logout.component";
import { AuthGuard } from "./admin/auth.guard";
import { NotFoundComponent } from "./pages/not-found/not-found.component";
import { environment } from "../environments/environment";
import { SaveComponent } from "./admin/components/save/save.component";
import { ImpressumComponent } from "./pages/impressum/impressum.component";
import { DataPrivacyComponent } from "./pages/data-privacy/data-privacy.component";

import { ConsentManagementService } from "./services/consent-management.service";
import { filter, map, mergeMap, Subscription } from "rxjs";
import { PaymentInfoComponent } from "./admin/components/payment-info/payment-info.component";
import { SpecialOpeningHoursDialogService } from "./services/special-opening-hours-dialog.service";
import { NotificationSpeicalOpeningHoursComponent } from "./components/meta/notification-speical-opening-hours/notification-speical-opening-hours.component";
import { UserinfoComponent } from "./admin/components/userinfo/userinfo.component";

import { Title } from '@angular/platform-browser';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    RouterOutlet,
    UserinfoComponent,
    UserinfoFaqComponent,
    PaymentInfoComponent,
    NotificationSpeicalOpeningHoursComponent,
    LogoutComponent,
    SaveComponent,
    DragDropModule,
    ToggleEditModeComponent,
    WebsiteConfigComponent,
  ],
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
  title = 'flower_stores';
  #themeService = inject(ThemeService);
  #renderer = inject(Renderer2);

  #pages: Signal<Record<string, Page>> = this.stateService.pages;
  #pageKeys: Signal<string[]> = this.stateService.pageKeys;
  #theming: Signal<Theming> = this.stateService.theming;

  public loggedIn: Signal<boolean> = this.userStateService.loggedIn;
  public isAdminPage = environment.isAdminPage
  public showAdminPanel = false;
  public isLoaded = false;

  private breakpointSubscription: Subscription = new Subscription();


  constructor(
    private breakpointObserver: BreakpointObserver,
    private router: Router,
    private route: ActivatedRoute,
    private auth: Auth,
    private userStateService: UserStateService,
    private stateService: WebsiteStateService,
    private consentManagementService: ConsentManagementService,
    public specialOpeningHoursDialogService: SpecialOpeningHoursDialogService,
    private titleService: Title,
    @Inject(PLATFORM_ID) private platformId: Object
  ) { 
    
    this.titleService.setTitle(this.stateService.websiteData().name);
    
    effect(() => {
      console.log('Update theming');
      this.#updateTheming();
    });

    effect(() => {
      if (isPlatformBrowser(this.platformId)) {
        this.#triggerUpdateRoutes();
      } else {
        console.warn('Skipping effects: not running in the browser.');
      }
    });

  }
  
  ngOnInit() {
    onAuthStateChanged(this.auth, (user) => {
      if (user) {
        this.userStateService.setUser({
          displayName: user.displayName,
          email: user.email,
          emailVerified: user.emailVerified,
          isAnonymous: user.isAnonymous,
          metadata: user.metadata,
          phoneNumber: user.phoneNumber,
          photoURL: user.photoURL
        });
      } else {
        this.userStateService.setUser({
          displayName: null,
          email: null,
          emailVerified: false,
          isAnonymous: false,
          metadata: null,
          phoneNumber: null,
          photoURL: null
        });
      }
    });

    this.#triggerUpdateRoutes();
    // this.#updateTheming();

    this.breakpointSubscription = this.breakpointObserver.observe([
      Breakpoints.HandsetPortrait,
      Breakpoints.HandsetLandscape,
      Breakpoints.TabletPortrait,
      Breakpoints.TabletLandscape,
      Breakpoints.WebPortrait,
      Breakpoints.WebLandscape
    ]).subscribe((state: BreakpointState) => {
      if (state.matches) {
        this.setBodyClass(state);
      }
    });

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map(() => this.route), 
        map((route) => {
          while (route.firstChild) {
            route = route.firstChild;
          }
          return route;
        }),
        mergeMap((route) => route.data)
      )
      .subscribe((data) => {
        this.showAdminPanel = data.hasOwnProperty('showAdminPanel') ? data['showAdminPanel'] : true;
        const url = this.router.url.split('?')[0]; // URL ohne Query-Parameter
        this.stateService.setActivePageByUrl(url); // Setzt den aktiven Zustand im StateService
        if (typeof window !== 'undefined') {
          window.scrollTo(0, 0); // Scrollt zum Anfang
        }
      });
  }

  ngAfterViewInit() {
    
    if (isPlatformBrowser(this.platformId)) {
      this.consentManagementService.initCookieConsent();
      this.specialOpeningHoursDialogService.notificationDialog();
    } 
  
  }

  ngOnDestroy() {
    this.breakpointSubscription.unsubscribe();
  }


  setBodyClass(state: BreakpointState) {
    this.#renderer.removeClass(document.body, 'handset-portrait');
    this.#renderer.removeClass(document.body, 'handset-landscape');
    this.#renderer.removeClass(document.body, 'tablet-portrait');
    this.#renderer.removeClass(document.body, 'tablet-landscape');
    this.#renderer.removeClass(document.body, 'web-portrait');
    this.#renderer.removeClass(document.body, 'web-landscape');

    if (state.breakpoints[Breakpoints.HandsetPortrait]) {
      this.#renderer.addClass(document.body, 'handset-portrait');
    } else if (state.breakpoints[Breakpoints.HandsetLandscape]) {
      this.#renderer.addClass(document.body, 'handset-landscape');
    } else if (state.breakpoints[Breakpoints.TabletPortrait]) {
      this.#renderer.addClass(document.body, 'tablet-portrait');
    } else if (state.breakpoints[Breakpoints.TabletLandscape]) {
      this.#renderer.addClass(document.body, 'tablet-landscape');
    } else if (state.breakpoints[Breakpoints.WebPortrait]) {
      this.#renderer.addClass(document.body, 'web-portrait');
    } else if (state.breakpoints[Breakpoints.WebLandscape]) {
      this.#renderer.addClass(document.body, 'web-landscape');
    }
  }


  #triggerUpdateRoutes() {

    if (this.#pages() && this.#pageKeys().length > 0) {
      // ACTUAL WEBSITE PAGES
      const dynamicRoutes: Route[] = Object.values(this.#pages()).map(page => ({
        path: page.url,
        component: PageComponent,
        canActivate: [AuthGuard],
        data: { title: 'Example Page', showAdminPanel: true },
      }));

      // ONLY ADMIN PAGES
      if (this.isAdminPage) {
        dynamicRoutes.push({ path: '', loadChildren: () => import('./admin/admin.module').then((m) => m.AdminModule) });
        // dynamicRoutes.push({ path: 'utils/ui', component: TestingComponent, canActivate: [AuthGuard] });
      }

      // FIX PAGES
      dynamicRoutes.push({ path: 'impressum', component: ImpressumComponent, canActivate: [AuthGuard], data: { title: 'Impressum', showAdminPanel: false }, });
      dynamicRoutes.push({ path: 'datenschutz', component: DataPrivacyComponent, canActivate: [AuthGuard], data: { title: 'Datenschutzerklärung', showAdminPanel: false }, });
      dynamicRoutes.push({ path: '**', component: NotFoundComponent, canActivate: [AuthGuard], data: { title: 'Example Page', showAdminPanel: false }, });

      // Setze die dynamischen Routen
      this.router.resetConfig(dynamicRoutes);
      this.isLoaded = true;
    } else {
      console.error('Keine Seiten gefunden!');
    }
  }

  #updateTheming() {
    this.#themeService.updateTheming(this.#theming());
  }
}